import type { AlertSeverity } from "../types/alerts";

export const ALERT_MUTE_STORAGE_KEY = "vtms.alerts.muted";

const RATE_LIMIT_MS = 2_000;
const TONE_HZ: readonly [number, number] = [880, 1320];
const TONE_MS = 110;
const TONE_GAIN = 0.18;

export interface AlertSounder {
  /** Resume the audio context; browsers require this from a user gesture. */
  arm: () => void;
  /** Beep for `critical` only, at most once per 2 s while unmuted. */
  play: (severity: AlertSeverity) => boolean;
  setMuted: (muted: boolean) => void;
  readonly muted: boolean;
}

export interface AlertSounderOptions {
  now?: () => number;
  createContext?: () => AudioContext | null;
}

function readMuted(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(ALERT_MUTE_STORAGE_KEY) === "1";
  } catch (err) {
    console.warn(`[alerts] localStorage read failed for ${ALERT_MUTE_STORAGE_KEY}`, err);
    return false;
  }
}

function writeMuted(muted: boolean): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(ALERT_MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch (err) {
    console.warn(`[alerts] localStorage write failed for ${ALERT_MUTE_STORAGE_KEY}`, err);
  }
}

function defaultCreateContext(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  try {
    return new AudioContext();
  } catch (err) {
    console.warn("[alerts] AudioContext unavailable", err);
    return null;
  }
}

function scheduleTone(ctx: AudioContext, frequency: number, startAt: number, durationS: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(TONE_GAIN, startAt + 0.01);
  gain.gain.setValueAtTime(TONE_GAIN, startAt + durationS - 0.02);
  gain.gain.linearRampToValueAtTime(0, startAt + durationS);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durationS);
}

export function createAlertSounder(options: AlertSounderOptions = {}): AlertSounder {
  const now = options.now ?? (() => Date.now());
  const createContext = options.createContext ?? defaultCreateContext;
  let ctx: AudioContext | null = null;
  let muted = readMuted();
  let lastPlayedAt = -Infinity;

  const ensureContext = (): AudioContext | null => {
    if (!ctx) ctx = createContext();
    return ctx;
  };

  const arm = () => {
    const c = ensureContext();
    if (c && c.state === "suspended") {
      c.resume().catch((err: unknown) => console.warn("[alerts] AudioContext resume failed", err));
    }
  };

  const play = (severity: AlertSeverity): boolean => {
    if (severity !== "critical" || muted) return false;
    const t = now();
    if (t - lastPlayedAt < RATE_LIMIT_MS) return false;
    const c = ensureContext();
    if (!c) return false;
    lastPlayedAt = t;
    const durationS = TONE_MS / 1000;
    const start = c.currentTime;
    scheduleTone(c, TONE_HZ[0], start, durationS);
    scheduleTone(c, TONE_HZ[1], start + durationS, durationS);
    return true;
  };

  const setMuted = (next: boolean) => {
    muted = next;
    writeMuted(next);
  };

  return {
    arm,
    play,
    setMuted,
    get muted() {
      return muted;
    },
  };
}
